import { useEffect, useState, useCallback } from "react";
import { Save, FilePlus2, Users, Bell, FileText, CalendarPlus, Tag, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import MetabolismTab from "@/components/lab/MetabolismTab";
import ClinicaTab from "@/components/lab/ClinicaTab";
import PianoTab from "@/components/lab/PianoTab";
import ConfrontoTab from "@/components/lab/ConfrontoTab";
import MisurazioniTab, { emptyMeasurements } from "@/components/lab/MisurazioniTab";
import AnamnesiTab, { emptyAnamnesi } from "@/components/lab/AnamnesiTab";
import FotoTab, { emptyFoto } from "@/components/lab/FotoTab";
import AllenamentoTab, { emptyAllenamento } from "@/components/lab/AllenamentoTab";
import PatientsDrawer from "@/components/lab/PatientsDrawer";
import NotificheDrawer, { computeReminders } from "@/components/lab/NotificheDrawer";
import ReportDrawer from "@/components/lab/ReportDrawer";
import AppuntamentiDrawer from "@/components/lab/AppuntamentiDrawer";
import OfferteDrawer from "@/components/lab/OfferteDrawer";
import SicurezzaDrawer from "@/components/lab/SicurezzaDrawer";
import PrivateLock from "@/components/lab/PrivateLock";
import PatientLinkButton from "@/components/lab/PatientLinkButton";
import VideoAnalisiTab from "@/components/lab/VideoAnalisiTab";
import { planInfo } from "@/lib/plan";

import { makeDiet, cloneDiet } from "@/lib/diet";
import { resolveMacros } from "@/lib/calc";
import { exportPrivacyPDF } from "@/lib/pdf";
import * as api from "@/lib/api";

const TABS = [
  { id: 1, label: "Metabolismo & Macro" },
  { id: 2, label: "Clinica & PubMed" },
  { id: 3, label: "Piano Alimentare" },
  { id: 4, label: "Confronto" },
  { id: 5, label: "Misurazioni" },
  { id: 6, label: "Anamnesi" },
  { id: 7, label: "Foto" },
  { id: 8, label: "Allenamento" },
  { id: 9, label: "Analisi Video" },
];

const blankPatient = () => ({
  id: null,
  nome: "",
  cognome: "",
  sesso: "uomo",
  eta: "",
  mestiere: "",
  peso: "",
  altezza: "",
  massaGrassa: "",
  formula: "mifflin",
  laf: 1.55,
  macro: { calorieTarget: 2000, carbP: 40, protP: 30, fatP: 30 },
  clinica: { condizione: "", note: "", result: null, allergia: "", allergiaResult: null },
  diets: [makeDiet("Dieta A")],
  measurements: emptyMeasurements(),
  anamnesi: emptyAnamnesi(),
  foto: emptyFoto(),
  allenamento: emptyAllenamento(),
  plan: { start: "", end: "", value: "", unit: "settimane" },
});

// Ensure a loaded patient has all nested structures
const hydrate = (p) => ({
  ...blankPatient(),
  ...p,
  macro: { ...blankPatient().macro, ...(p.macro || {}) },
  clinica: { condizione: "", note: "", result: null, allergia: "", allergiaResult: null, ...(p.clinica || {}) },
  diets: p.diets && p.diets.length ? p.diets : [makeDiet("Dieta A")],
  measurements: p.measurements && p.measurements.type ? p.measurements : emptyMeasurements(),
  anamnesi: p.anamnesi && p.anamnesi.alimentare ? p.anamnesi : { ...emptyAnamnesi(), ...(p.anamnesi || {}), generali: { ...emptyAnamnesi().generali, ...((p.anamnesi && p.anamnesi.generali) || {}) } },
  foto: p.foto && p.foto.sections ? p.foto : emptyFoto(),
  allenamento: p.allenamento && p.allenamento.schede ? p.allenamento : emptyAllenamento(),
  plan: p.plan || { start: "", end: "", value: "", unit: "settimane" },
});

export default function NutriLab({ workspace = "main" }) {
  const [tab, setTab] = useState(1);
  const [patient, setPatient] = useState(blankPatient());
  const [foods, setFoods] = useState([]);
  const [patients, setPatients] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [offersOpen, setOffersOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const openPrivacy = async () => {
    if (!patient.id) { toast.error("Seleziona prima un paziente"); return; }
    try { const r = await api.getPatientPrivacy(patient.id); if (!exportPrivacyPDF(r)) toast.error("Consenti i popup per aprire il PDF"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Modulo privacy non disponibile"); }
  };
  const logout = async () => {
    try { await api.authLogout(); } catch (e) { console.error("Logout:", e); }
    window.location.replace("/admin-login");
  };
  const [reportOpen, setReportOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [focusApptId, setFocusApptId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [activeDiet, setActiveDiet] = useState(0);

  const refreshFoods = useCallback(async () => {
    try { setFoods(await api.listFoods()); } catch (e) { console.error("Errore caricamento alimenti:", e); }
  }, []);
  const refreshPatients = useCallback(async () => {
    try { setPatients(await api.listPatients()); } catch (e) { console.error("Errore caricamento pazienti:", e); }
  }, []);
  const refreshAppointments = useCallback(async () => {
    try { setAppointments(await api.listAppointments()); } catch (e) { console.error("Errore caricamento appuntamenti:", e); }
  }, []);

  const [pendingProg, setPendingProg] = useState([]);
  const refreshPendingProg = useCallback(async () => {
    try { setPendingProg(await api.listPendingProgress()); } catch (e) { console.error("Errore progressioni:", e); }
  }, []);

  useEffect(() => { window.addEventListener("foods-changed", refreshFoods); return () => window.removeEventListener("foods-changed", refreshFoods); }, [refreshFoods]);
  useEffect(() => { refreshFoods(); refreshPatients(); refreshAppointments(); refreshPendingProg(); }, [refreshFoods, refreshPatients, refreshAppointments, refreshPendingProg]);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [focusVideoId, setFocusVideoId] = useState(null);
  const refreshPendingVideos = useCallback(async () => {
    try { setPendingVideos(await api.listPendingVideos()); } catch (e) { console.error("Errore video:", e); }
  }, []);
  const [pendingComments, setPendingComments] = useState([]);
  const refreshPendingComments = useCallback(async () => {
    try { setPendingComments(await api.listPendingComments()); } catch (e) { console.error("Errore note:", e); }
  }, []);
  const [pendingMeasures, setPendingMeasures] = useState([]);
  const [apptEvents, setApptEvents] = useState([]);
  const refreshApptEvents = useCallback(async () => {
    try { setApptEvents(await api.listApptEvents()); } catch (e) { console.error("Errore eventi appuntamenti:", e); }
  }, []);
  useEffect(() => { refreshApptEvents(); const t = setInterval(refreshApptEvents, 60000); return () => clearInterval(t); }, [refreshApptEvents]);
  const refreshPendingMeasures = useCallback(async () => {
    try { setPendingMeasures(await api.listPendingMeasurements()); } catch (e) { console.error("Errore misurazioni:", e); }
  }, []);
  useEffect(() => { refreshPendingVideos(); refreshPendingComments(); refreshPendingMeasures(); }, [refreshPendingVideos, refreshPendingComments, refreshPendingMeasures]);
  useEffect(() => { const t = setInterval(() => { refreshPendingProg(); refreshPendingVideos(); refreshPendingComments(); refreshPendingMeasures(); }, 60000); return () => clearInterval(t); }, [refreshPendingProg, refreshPendingVideos, refreshPendingComments, refreshPendingMeasures]);

  const update = (patch) => setPatient((p) => ({ ...p, ...patch }));
  const setMacro = (macro) => setPatient((p) => ({ ...p, macro }));
  const setClinica = (clinica) => setPatient((p) => ({ ...p, clinica }));
  const setDiets = (diets) => setPatient((p) => ({ ...p, diets }));
  const setMeasurements = (measurements) => setPatient((p) => ({ ...p, measurements }));
  const setAnamnesi = (anamnesi) => setPatient((p) => ({ ...p, anamnesi: typeof anamnesi === "function" ? anamnesi(p.anamnesi) : anamnesi }));
  const setFoto = (foto) => setPatient((p) => ({ ...p, foto }));
  const setAllenamento = (allenamento) => setPatient((p) => ({ ...p, allenamento }));
  const setPlan = (plan) => setPatient((p) => ({ ...p, plan }));

  const planCountdown = planInfo(patient);

  const importAnamnesiDiet = (diet) => {
    const copy = { ...cloneDiet(diet, `Anamnesi — ${diet.nome}`), fromAnamnesi: true };
    setPatient((p) => ({ ...p, diets: [...p.diets, copy] }));
    setActiveDiet(patient.diets.length);
    setTab(3);
    toast.success("Dieta importata nella Sezione 3 (Piano Alimentare)");
  };

  const macroGrams = resolveMacros(patient);

  const reminderCount = computeReminders(patients, pendingProg, pendingVideos, pendingComments, pendingMeasures, apptEvents).total;
  const weekApptCount = (() => {
    const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const end = new Date(start); end.setDate(end.getDate() + 7);
    return appointments.filter((a) => {
      if (!a.date || !(a.email || "").trim()) return false;
      const d = new Date(`${a.date}T00:00:00`);
      return d >= start && d < end;
    }).length;
  })();

  const openPatientFromNotif = (id, target) => {
    if (target === "apptevent") {
      const [eid, aid] = id.split("|");
      api.markApptEventSeen(eid).then(refreshApptEvents).catch(() => {});
      setNotifOpen(false); setFocusApptId(aid); refreshAppointments(); setApptOpen(true);
      return;
    }
    if (target === "appuntamenti") {
      setNotifOpen(false);
      setFocusApptId(id);
      refreshAppointments();
      setApptOpen(true);
      return;
    }
    let pid = id;
    if (target === "video") { const [p0, vid] = id.split("|"); pid = p0; setFocusVideoId(vid); target = 9; }
    let incoming = null;
    if (target === "measures") {
      const [p0, mid] = id.split("|"); pid = p0; target = 5;
      incoming = pendingMeasures.find((m) => m.id === mid);
    }
    if (target === "comments") {
      target = 8;
      api.markCommentsSeen(`wk_${pid}`).then(refreshPendingComments).catch((e) => console.error("Note:", e));
    }
    const p = patients.find((x) => x.id === pid);
    if (!p) return;
    handleLoad(p);
    if (incoming) setPatient((cur) => {
      const m = cur.measurements || {};
      if ((m.antropo || []).some((w) => w.incoming === incoming.id)) return { ...cur, measurements: { ...m, type: "antropo" } };
      const week = { id: Math.random().toString(36).slice(2, 10), date: incoming.date, note: incoming.note || "", incoming: incoming.id, values: { PESO: String(incoming.peso), ...Object.fromEntries(Object.entries(incoming.avg).map(([k, v]) => [k, String(v)])) } };
      return { ...cur, measurements: { ...m, type: "antropo", antropo: [...(m.antropo || []), week] } };
    });
    setNotifOpen(false);
    if (target === "report") setReportOpen(true);
    else if (target) setTab(target);
  };

  const createFood = async (form) => {
    const created = await api.createFood(form);
    await refreshFoods();
    return created;
  };

  const confirmIncoming = async (weekId) => {
    const m = patient.measurements;
    const w = m.antropo.find((x) => x.id === weekId);
    const { incoming, ...clean } = w;
    const next = { ...patient, measurements: { ...m, antropo: m.antropo.map((x) => (x.id === weekId ? clean : x)) } };
    try {
      await api.savePatient(next);
      await api.confirmMeasurement(incoming);
      setPatient(next);
      refreshPatients(); refreshPendingMeasures();
      toast.success("Misurazione confermata e salvata");
    } catch (e) { toast.error("Errore durante la conferma"); }
  };

  const handleSave = async () => {
    if (!patient.cognome.trim() && !patient.nome.trim()) {
      toast.error("Inserisci almeno il cognome del paziente");
      return;
    }
    try {
      const saved = await api.savePatient(patient);
      setPatient((p) => ({ ...p, id: saved.id, created_at: saved.created_at }));
      await refreshPatients();
      toast.success("Paziente salvato");
    } catch (e) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleNew = () => {
    setPatient(blankPatient());
    setActiveDiet(0);
    setTab(1);
    toast.success("Nuova scheda paziente");
  };

  const handleLoad = (p) => {
    setPatient(hydrate(p));
    setActiveDiet(0);
    setDrawer(false);
    toast.success(`Caricato: ${[p.cognome, p.nome].filter(Boolean).join(" ")}`);
  };

  const handleDelete = async (p) => {
    try {
      await api.deletePatient(p.id);
      if (p.id === patient.id) handleNew();
      await refreshPatients();
      toast.success("Paziente eliminato");
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[hsl(var(--background))]/85 backdrop-blur-md">
        <div className={`mx-auto flex ${(tab === 8 || tab === 4) ? "max-w-screen-2xl" : "max-w-6xl"} flex-wrap items-center justify-between gap-3 px-4 py-3`}>
          <div className="flex items-center gap-3">
            <img src="/agfit-logo.png" alt="AGFIT" className="h-11 w-11 rounded-xl object-cover ring-1 ring-[#d4af37]/40" data-testid="app-logo" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-lg font-extrabold leading-none" data-testid="app-title">AGFIT</h1>
                <PrivateLock workspace={workspace} />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="app-subtitle">Professional Coaching a cura di Alex Grisi &amp; Franco Lenzi</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input value={patient.nome} onChange={(e) => update({ nome: e.target.value })} placeholder="Nome" className="w-28 rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm outline-none focus:border-primary/60" data-testid="nome-input" />
            <input value={patient.cognome} onChange={(e) => update({ cognome: e.target.value })} placeholder="Cognome" className="w-32 rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm outline-none focus:border-primary/60" data-testid="cognome-input" />
            <button onClick={handleNew} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="new-patient-btn">
              <FilePlus2 className="h-4 w-4" /> Nuovo
            </button>
            <button onClick={handleSave} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="save-patient-btn">
              <Save className="h-4 w-4" /> Salva
            </button>
            <PatientLinkButton patientId={patient.id} patientName={patient.nome || "Cliente"} />
            <button onClick={openPrivacy} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(199_89%_55%)]/50 bg-[hsl(199_89%_55%)]/10 px-3 py-2 text-sm font-semibold text-[hsl(199_89%_68%)] transition-colors hover:bg-[hsl(199_89%_55%)]/20" data-testid="open-privacy-btn">
              <ShieldCheck className="h-4 w-4" /> PRIVACY
            </button>
            <button onClick={() => { refreshPatients(); setFocusApptId(null); setApptOpen(true); }} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--c-teal))]/40 bg-[hsl(var(--c-teal))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--c-teal))] transition-colors hover:bg-[hsl(var(--c-teal))]/20" data-testid="open-appuntamenti-btn">
              <CalendarPlus className="h-4 w-4" /> Appuntamenti
              <span className="rounded-md bg-[hsl(var(--c-teal))]/20 px-1.5 py-0.5 text-[10px] font-bold" title="Appuntamenti con email in questa settimana" data-testid="week-appt-count">{weekApptCount}</span>
            </button>
            <button onClick={() => setReportOpen(true)} className="relative inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="open-report-btn">
              <FileText className="h-4 w-4" /> Report
              {planCountdown.hasPlan && (
                <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${planCountdown.expired ? "bg-[hsl(0_72%_60%)]/20 text-[hsl(0_72%_68%)]" : "bg-primary/15 text-primary"}`} data-testid="plan-countdown-badge">
                  {planCountdown.expired ? "Scaduto" : `${planCountdown.daysLeft}g`}
                </span>
              )}
            </button>
            <button onClick={() => { refreshPatients(); setDrawer(true); }} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="open-patients-btn">
              <Users className="h-4 w-4" /> Pazienti
              <span className="rounded-md bg-primary/15 px-1.5 py-0.5 text-xs font-bold text-primary">{patients.length}</span>
            </button>
            <button onClick={() => { refreshPatients(); refreshAppointments(); refreshPendingProg(); refreshPendingVideos(); refreshPendingComments(); refreshPendingMeasures(); refreshApptEvents(); setNotifOpen(true); }} className="relative inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="open-notifiche-btn">
              <Bell className="h-4 w-4" /> Notifica
              {reminderCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-[hsl(var(--c-fat))] px-1 text-[10px] font-bold text-black" data-testid="notif-badge">{reminderCount}</span>
              )}
            </button>
            <button onClick={() => setOffersOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--c-fat))]/40 bg-[hsl(var(--c-fat))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--c-fat))] transition-colors hover:bg-[hsl(var(--c-fat))]/20" data-testid="open-offerte-btn">
              <Tag className="h-4 w-4" /> Offerte
            </button>
            <button onClick={() => setSecOpen(true)} title="Sicurezza e backup" className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium transition-colors hover:bg-white/5" data-testid="open-sicurezza-btn">
              <ShieldCheck className="h-4 w-4" /> Sicurezza
            </button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(0_72%_60%)]/40 px-3 py-2 text-sm font-medium text-[hsl(0_72%_68%)] transition-colors hover:bg-[hsl(0_72%_60%)]/10" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className={`mx-auto ${(tab === 8 || tab === 4) ? "max-w-screen-2xl" : "max-w-6xl"} px-4 pt-5`}>
        <div className="flex gap-1 overflow-x-auto rounded-xl border border-white/5 bg-black/20 p-1.5" data-testid="tab-bar">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-active={tab === t.id} className="tab-pill" data-testid={`tab-${t.id}`}>
              {t.id} · {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className={`mx-auto ${(tab === 8 || tab === 4) ? "max-w-screen-2xl" : "max-w-6xl"} px-4 py-6`}>
        {tab === 1 && <MetabolismTab patient={patient} update={update} macro={patient.macro} setMacro={setMacro} />}
        {tab === 2 && <ClinicaTab clinica={patient.clinica} setClinica={setClinica} />}
        {tab === 3 && (
          <PianoTab
            diets={patient.diets}
            setDiets={setDiets}
            foods={foods}
            onCreateFood={createFood}
            activeDiet={activeDiet}
            setActiveDiet={setActiveDiet}
            macroGrams={macroGrams}
            calorieTarget={macroGrams.kcal}
          />
        )}
        {tab === 4 && <ConfrontoTab diets={patient.diets} macroGrams={macroGrams} calorieTarget={macroGrams.kcal} />}
        {tab === 5 && <MisurazioniTab measurements={patient.measurements} setMeasurements={setMeasurements} onSave={handleSave} onConfirmIncoming={confirmIncoming} />}
        {tab === 6 && (
          <AnamnesiTab
            anamnesi={patient.anamnesi}
            setAnamnesi={setAnamnesi}
            foods={foods}
            onCreateFood={createFood}
            onImportDiet={importAnamnesiDiet}
            macroGrams={macroGrams}
          />
        )}
        {tab === 7 && <FotoTab foto={patient.foto} setFoto={setFoto} onSave={handleSave} />}
        {tab === 9 && <VideoAnalisiTab patientId={patient.id} focusVideoId={focusVideoId} onCorrected={refreshPendingVideos} />}
        {tab === 8 && <AllenamentoTab allenamento={patient.allenamento} setAllenamento={setAllenamento} onSave={handleSave} patientName={[patient.cognome, patient.nome].filter(Boolean).join(" ") || "Cliente"} patientId={patient.id} />}
      </main>

      <PatientsDrawer open={drawer} setOpen={setDrawer} patients={patients} onLoad={handleLoad} onDelete={handleDelete} currentId={patient.id} />
      <NotificheDrawer open={notifOpen} setOpen={setNotifOpen} patients={patients} pendingProgress={pendingProg} pendingVideos={pendingVideos} pendingComments={pendingComments} pendingMeasures={pendingMeasures} apptEvents={apptEvents} onOpenPatient={openPatientFromNotif} />
      <ReportDrawer open={reportOpen} setOpen={setReportOpen} patient={patient} setPlan={setPlan} onSave={handleSave} />
      <OfferteDrawer open={offersOpen} setOpen={setOffersOpen} patients={patients} />
      <SicurezzaDrawer open={secOpen} setOpen={setSecOpen} onRestored={refreshPatients} />
      <AppuntamentiDrawer open={apptOpen} setOpen={setApptOpen} patients={patients} onChanged={refreshAppointments} focusId={focusApptId} />
    </div>
  );
}
